import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { colors, spacing, typography } from '../../../theme-v2';
import { ReferenceCard } from './ReferenceCard';
import type { ChatSource } from './types';

export interface ReferenceCardsProps {
  sources: ChatSource[];
  /** True while the answer streams and sources haven't settled yet. */
  processing?: boolean;
  onSourceClick?: (source: ChatSource) => void;
  /** Id of the document currently shown — drives the "other document" label. */
  displayedDocumentId?: string;
  /** All documents, used to resolve a source's document name. */
  documents?: { id: string; name?: string }[];
}

/**
 * ReferenceCards — the "Sources" section rendered beneath a chatbot answer.
 * Shows a "processing" label while sources resolve, then a responsive grid of
 * ReferenceCards sorted by page. Clicking a card calls `onSourceClick` and
 * marks that card active.
 */
export const ReferenceCards = ({
  sources,
  processing,
  onSourceClick,
  displayedDocumentId,
  documents,
}: ReferenceCardsProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const [activeId, setActiveId] = useState<string | undefined>(undefined);

  const hasSources = sources.length > 0;
  if (!hasSources && !processing) return null;

  const sorted = [...sources].sort(
    (a, b) => (a.pageNumber ?? 0) - (b.pageNumber ?? 0),
  );
  const nameFor = (id: string) => documents?.find((d) => d.id === id)?.name;

  return (
    <StyledWrapper>
      {processing && !hasSources ? (
        <StyledProcessing>
          {t('experienceV2.chatbot.sources.processing')}
        </StyledProcessing>
      ) : null}
      {hasSources ? (
        <>
          <StyledTitle>{t('experienceV2.chatbot.sources.title')}</StyledTitle>
          <StyledGrid>
            {sorted.map((source) => (
              <ReferenceCard
                key={source.id}
                quote={source.quote}
                pageLabel={
                  source.pageNumber != null
                    ? t('experienceV2.chatbot.sources.page', {
                        page: source.pageNumber,
                      })
                    : undefined
                }
                documentName={
                  source.documentId !== displayedDocumentId
                    ? nameFor(source.documentId)
                    : undefined
                }
                active={source.id === activeId}
                onClick={() => {
                  setActiveId(source.id);
                  onSourceClick?.(source);
                }}
              />
            ))}
          </StyledGrid>
        </>
      ) : null}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  margin-top: ${spacing.sm}px;
`;

const StyledTitle = styled.span`
  display: block;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing.xs}px;
`;

const StyledProcessing = styled.span`
  display: block;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  color: ${colors.text.secondary};
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${spacing.sm}px;
`;
